import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Divider,
  Switch,
  Button,
  Paper,
  useTheme,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PrintersListByBranch from "./Printer/PrintersListByBranch";
import CashierListByBranch from "./CashireSetting/CashierListByBranch"; // ✅ تأكد من المسار الصحيح

import { fetchSettings, editPrintingMethod, editPrintInvoiceAfterSaving, editOrderPrintNumber } from "../../../../services/Settings/InvoiceAndTaghezPrintingApi";
import PrintIcon from "@mui/icons-material/Print";
import CheckIcon from "@mui/icons-material/Check";

const PrinterSettings = () => {
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(0);
  const [ctrlPPrint, setCtrlPPrint] = useState(false);
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [printingMethod, setPrintingMethod] = useState("manuel"); // جديد: حالة طريقة الطباعة ('manuel' | 'automatic')
  const [printAfterSave, setPrintAfterSave] = useState(false); // جديد: حالة خيار الطباعة بعد الحفظ
  const [orderPrintNumber, setOrderPrintNumber] = useState(1); // جديد: عدد مرات الطباعة قبل الحفظ
  const [orderPrintInput, setOrderPrintInput] = useState(1); // قيمة الحقل القابلة للتحرير قبل التأكيد
  const [orderPrintSaving, setOrderPrintSaving] = useState(false); // حالة حفظ عدد الطباعة عند الضغط تأكيد
  const [orderPrintSuccess, setOrderPrintSuccess] = useState(false); // إظهار نجاح مؤقت بعد التأكيد
  // NEW: pending confirmation state for printing method
  const [pendingPrintingMethod, setPendingPrintingMethod] = useState(null);
  const [printingConfirmOpen, setPrintingConfirmOpen] = useState(false);
  const [printingSaveLoading, setPrintingSaveLoading] = useState(false);

  // مرجع لتخطي نداء editPrintInvoiceAfterSaving خلال التهيئة الأولية
  const printAfterSaveInitialRef = useRef(true);

  // عند تغيّر خيار "الطباعة بعد الحفظ" من قبل المستخدم، نحدّث السيرفر تلقائياً
  useEffect(() => {
    // تجاهل التغيير الأولي عند ضبط القيمة من الخادم
    if (printAfterSaveInitialRef.current) {
      printAfterSaveInitialRef.current = false;
      return;
    }

    let mounted = true;
    const applyPrintAfterSave = async () => {
      try {
        await editPrintInvoiceAfterSaving(printAfterSave ? 1 : 0);
        if (!mounted) return;
        // يمكن إضافة إشعار نجاح هنا إذا رغبت
      } catch (err) {
        console.warn("Failed to auto-update print-after-save setting:", err);
      }
    };
    applyPrintAfterSave();

    return () => {
      mounted = false;
    };
  }, [printAfterSave]);

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const res = await fetchSettings();
        // دعم أشكال الاستجابة المختلفة:
        // 1) { data: { setting: { ... } } }
        // 2) { setting: { ... } }
        // 3) direct setting object
        const s =
          (res && res.data && res.data.setting) ||
          (res && res.setting) ||
          res ||
          {};

        setSettings(s);
        setCtrlPPrint(Number(s.ctrl_p_print) === 1);
        // تحميل طريقة الطباعة الحالية من الإعدادات إن وجدت
        setPrintingMethod(s.printing_method || "manuel");
        // تحميل خيار الطباعة بعد الحفظ إن وجد (print_invoice_after_saving)
        setPrintAfterSave(Number(s.print_invoice_after_saving) === 1);
        // تحميل عدد مرات الطباعة قبل الحفظ إن وجد (order_print_number) — تحويل من "0.00" أو نص إلى رقم
        const parsedOrderPrint = s.order_print_number != null ? Number(parseFloat(s.order_print_number)) : 1;
        setOrderPrintNumber(parsedOrderPrint);
        setOrderPrintInput(parsedOrderPrint); // تهيئة حقل الإدخال بقيمة الإعداد الحالي
      } catch (error) {
        setSettings({});
        setCtrlPPrint(true);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Tabs value={tabIndex} onChange={handleTabChange} centered>
        <Tab label="إعدادات طباعة" />
        <Tab label="قائمة الطابعات" />
        <Tab label="إعدادات الكاشير" />
      </Tabs>

      <Box sx={{ mt: 3 }}>
        {tabIndex === 0 && (
          <Paper
            elevation={8}
            sx={{
              mt: 2,
              maxWidth: 420,
              mx: "auto",
              p: { xs: 2, sm: 4 },
              borderRadius: 5,
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, #23272f 0%, #1a1a1a 100%)"
                  : "linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)",
              boxShadow: "0 8px 32px rgba(33,150,243,0.12)",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <PrintIcon
                sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.mode === "dark" ? "#90caf9" : "#1976d2",
                  letterSpacing: 1,
                  mb: 1,
                }}
              >
                اعدادات الطباعة {" "}
              </Typography>
              <Typography
                sx={{ color: theme.palette.text.secondary, fontSize: "1rem" }}
              >
         هنا يمكنك التحكم في طريقة الطباعه وطباعه بعد الحفظ و عدد مرات الطباعه قبل الحفظ
              </Typography>
            </Box>
            <Stack spacing={3} sx={{ mb: 2 }}>

              {/* عرض الحالة الحالية لوضع الطباعة اليدوي */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  background: theme.palette.mode === "dark" ? "#1f2730" : "#fafafa",
                }}
              >
                <Typography sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                  وضع الطباعة اليدوي
                </Typography>
                <Typography sx={{ marginLeft: "auto", color: theme.palette.text.secondary }}>
                  القيمة الحالية: {settings && settings.ctrl_p_print != null ? (Number(settings.ctrl_p_print) === 1 ? "نعم" : "لا") : "—"}
                </Typography>
              </Box>
              
               {/* اختيار طريقة الطباعة: تلقائي أم يدوي */}
               <FormControl component="fieldset" sx={{ px: 1 }}>
                 <FormLabel component="legend" sx={{ mb: 1, color: theme.palette.text.primary }}>
                   طريقة الطباعة
                 </FormLabel>
                 <RadioGroup
                   row
                   value={printingMethod}
                   onChange={(e) => {
                     // don't apply immediately — open confirm dialog
                     setPendingPrintingMethod(e.target.value);
                     setPrintingConfirmOpen(true);
                   }}
                 >
                   <FormControlLabel
                     value="automatic"
                     control={<Radio color="primary" />}
                     label="تلقائي"
                   />
                   <FormControlLabel
                     value="manuel"
                     control={<Radio color="primary" />}
                     label="طباعة بدون تحقق"
                   />
                 </RadioGroup>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                  القيمة الحالية: {settings && settings.printing_method ? settings.printing_method : "—"}
                </Typography>
               </FormControl>

               {/* خيار: الطباعة بعد الحفظ */}
               <Box
                 sx={{
                   display: "flex",
                   alignItems: "center",
                   gap: 2,
                   px: 2,
                   py: 1.5,
                   borderRadius: 3,
                   background:
                     theme.palette.mode === "dark" ? "#23272f" : "#f7fbfc",
                   boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                 }}
               >
                 <Typography sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                   الطباعة بعد الحفظ
                 </Typography>
                 <Switch
                   checked={printAfterSave}
                   onChange={(e) => setPrintAfterSave(e.target.checked)}
                   color="primary"
                   sx={{
                     marginLeft: "auto",
                     "& .MuiSwitch-thumb": {
                       bgcolor:
                         theme.palette.mode === "dark" ? "#90caf9" : "#1976d2",
                     },
                     "& .MuiSwitch-track": {
                       bgcolor:
                         theme.palette.mode === "dark" ? "#23272f" : "#90caf9",
                     },
                   }}
                 />
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, ml: 2 }}>
                  القيمة الحالية: {settings && settings.print_invoice_after_saving != null ? (Number(settings.print_invoice_after_saving) === 1 ? "نعم" : "لا") : "—"}
                </Typography>
               </Box>

               {/* عدد مرات الطباعة قبل الحفظ */}
               <Box
                 sx={{
                   display: "flex",
                   alignItems: "center",
                   gap: 2,
                   px: 2,
                   py: 1.5,
                   borderRadius: 3,
                   background:
                     theme.palette.mode === "dark" ? "#23272f" : "#f7fbfc",
                   boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                 }}
               >
                 <Typography sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                   عدد مرات الطباعة قبل الحفظ
                 </Typography>
                {/* حقل قابل للتعديل + زر تأكيد */}
                <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
                  <TextField
                    type="number"
                    inputProps={{ min: 0 }}
                    value={orderPrintInput}
                    onChange={(e) => setOrderPrintInput(e.target.value)}
                    size="small"
                    sx={{ width: 120, bgcolor: 'transparent' }}
                  />
                  {/* زر التأكيد يظهر فقط عند اختلاف القيمة عن القيمة المحفوظة */}
                  {String(orderPrintInput) !== String(orderPrintNumber) && (
                    <Tooltip title="تأكيد عدد مرات الطباعة">
                      <span>
                        <IconButton
                          color="primary"
                          onClick={async () => {
                            const val = Number(orderPrintInput) || 0;
                            setOrderPrintSaving(true);
                            setOrderPrintSuccess(false);
                            try {
                              await editOrderPrintNumber(val);
                              // تحديث الحالة بعد النجاح
                              setOrderPrintNumber(val);
                              setSettings(prev => ({ ...prev, order_print_number: val }));
                              setOrderPrintSuccess(true);
                              setTimeout(() => setOrderPrintSuccess(false), 2500);
                            } catch (err) {
                              console.warn("Failed to update order print number:", err);
                              // يمكنك عرض رسالة خطأ هنا لو أردت
                            } finally {
                              setOrderPrintSaving(false);
                            }
                          }}
                          disabled={orderPrintSaving}
                          size="small"
                        >
                          {orderPrintSaving ? <CircularProgress size={18} /> : <CheckIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, ml: 2 }}>
                  القيمة الحالية: {settings && settings.order_print_number != null ? String(settings.order_print_number) : "—"}
                </Typography>
               </Box>
             </Stack>
           
            
          </Paper>
        )}
        {tabIndex === 1 && (
          <>
            {ctrlPPrint && (
              <Typography
                sx={{
                  mb: 2,
                  color: "#d32f2f",
                  background: "#fff3e0",
                  borderRadius: 2,
                  py: 1,
                  px: 2,
                  fontWeight: "bold",
                  textAlign: "center",
                  boxShadow: "0 2px 8px rgba(255,152,0,0.08)",
                }}
              >
                انك الآن في وضع الطباعة اليدوي، يرجى إلغاء تفعيلها لإضافة خاصية الطباعة التلقائية.
              </Typography>
            )}
            <Divider sx={{ my: 4 }} />
            <PrintersListByBranch />
          </>
        )}
        {tabIndex === 2 && (
          <>
            {ctrlPPrint && (
              <Typography
                sx={{
                  mb: 2,
                  color: "#d32f2f",
                  background: "#fff3e0",
                  borderRadius: 2,
                  py: 1,
                  px: 2,
                  fontWeight: "bold",
                  textAlign: "center",
                  boxShadow: "0 2px 8px rgba(255,152,0,0.08)",
                }}
              >
                انك الآن في وضع الطباعة اليدوي، يرجى إلغاء تفعيلها لإضافة خاصية الطباعة التلقائية.
              </Typography>
            )}
            <CashierListByBranch />
          </>
        )}
      </Box>

      {/* Confirmation dialog for printing method change */}
      <Dialog
        open={printingConfirmOpen}
        onClose={() => { setPrintingConfirmOpen(false); setPendingPrintingMethod(null); }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>تأكيد تغيير طريقة الطباعة</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography>هل ترغب بتغيير طريقة الطباعة من:</Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {printingMethod || "—"}
            </Typography>
            <Typography>إلى:</Typography>
            <Typography sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              {pendingPrintingMethod || "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              عند التأكيد سيتم حفظ الإعداد على الخادم.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPrintingConfirmOpen(false);
              setPendingPrintingMethod(null);
            }}
          >
            إلغاء
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!pendingPrintingMethod) return;
              setPrintingSaveLoading(true);
              try {
                await editPrintingMethod(pendingPrintingMethod);
                setPrintingMethod(pendingPrintingMethod);
                setSettings((prev) => ({ ...prev, printing_method: pendingPrintingMethod }));
                setPrintingConfirmOpen(false);
                setPendingPrintingMethod(null);
                setSuccessMsg("تم حفظ طريقة الطباعة بنجاح");
                setTimeout(() => setSuccessMsg(""), 2500);
              } catch (err) {
                console.warn("Failed to save printing method:", err);
                alert("فشل حفظ طريقة الطباعة");
              } finally {
                setPrintingSaveLoading(false);
              }
            }}
            disabled={printingSaveLoading}
          >
            {printingSaveLoading ? <CircularProgress size={18} /> : "تأكيد الحفظ"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrinterSettings;
